import React, { useMemo, useState } from 'react';

export type CellRenderer<T> = (value: any, row: T) => React.ReactNode;

export type ColumnDef<T> = {
  key: keyof T | string;
  header?: string;
  sortable?: boolean;
  filter?: 'text' | 'number' | 'date' | { type: 'select'; options: Array<string | number> };
  render?: CellRenderer<T>;
  width?: string;
  align?: 'left' | 'center' | 'right';
  hidden?: boolean;
};

export type DataTableProps<T extends Record<string, any>> = {
  data: T[];
  columns?: ColumnDef<T>[];
  pageSize?: number;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  onRowClick?: (row: T) => void;
  className?: string;
  dense?: boolean;
};

function getCellValue(row: any, key: string) {
  return key.split('.').reduce((acc, k) => acc?.[k], row);
}

function compare(a: any, b: any) {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  const na = typeof a === 'number' ? a : (typeof a === 'string' ? Number(a.replace(/[^0-9.-]+/g, '')) : NaN);
  const nb = typeof b === 'number' ? b : (typeof b === 'string' ? Number(b.replace(/[^0-9.-]+/g, '')) : NaN);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  const da = new Date(a as any).valueOf();
  const db = new Date(b as any).valueOf();
  if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db;
  return String(a).localeCompare(String(b), 'fr', { sensitivity: 'base', numeric: true });
}

export default function DataTable<T extends Record<string, any>>({
  data,
  columns,
  pageSize = 10,
  initialSort,
  onRowClick,
  className = '',
  dense = false,
}: DataTableProps<T>) {
  const inferredCols = useMemo<ColumnDef<T>[]>(() => {
    if (columns && columns.length) return columns;
    const first = data?.[0] ?? {};
    return Object.keys(first).map(k => ({ key: k, header: k, sortable: true, filter: 'text' }));
  }, [columns, data]);

  const [globalQuery, setGlobalQuery] = useState('');
  const [searchColumns, setSearchColumns] = useState<string[]>([]);
  const [showSearchColumns, setShowSearchColumns] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(initialSort ?? null);
  const [page, setPage] = useState(1);
  const [hidden, setHidden] = useState<Record<string, boolean>>(
    Object.fromEntries(inferredCols.map(c => [String(c.key), !!c.hidden]))
  );
  const [columnOrder, setColumnOrder] = useState<string[]>(
    inferredCols.map(c => String(c.key))
  );

  //const visibleCols = useMemo(() => inferredCols.filter(c => !hidden[String(c.key)]), [inferredCols, hidden]);
  const orderedCols = useMemo(() => {
    return columnOrder
      .map(key => inferredCols.find(c => String(c.key) === key))
      .filter((c): c is ColumnDef<T> => !!c);
  }, [columnOrder, inferredCols]);

  const visibleCols = useMemo(
    () => orderedCols.filter(c => !hidden[String(c.key)]),
    [orderedCols, hidden]
  );

  // 🔹 Filtrage global ou colonne spécifique
  const filtered = useMemo(() => {
    const q = globalQuery.trim().toLowerCase();
    const hasGlobal = q.length > 0;

    return data.filter(row => {
      // Filtres par colonne
      for (const col of inferredCols) {
        const k = String(col.key);
        const f = columnFilters[k];
        if (f != null && f !== '' && col.filter) {
          const v = getCellValue(row, k);
          if (typeof col.filter === 'object' && col.filter.type === 'select') {
            if (String(v) !== String(f)) return false;
          } else if (col.filter === 'number') {
            if (Number(v) !== Number(f)) return false;
          } else if (col.filter === 'date') {
            const dv = v ? new Date(v).toISOString().slice(0, 10) : '';
            if (dv !== f) return false;
          } else {
            if (!String(v ?? '').toLowerCase().includes(String(f).toLowerCase())) return false;
          }
        }
      }

      // 🔍 Recherche globale ou colonne spécifique
      if (hasGlobal) {
        const colsToSearch =
          searchColumns.length === 0
            ? inferredCols
            : inferredCols.filter(c => searchColumns.includes(String(c.key)));

        return colsToSearch.some(c => {
          const v = getCellValue(row, String(c.key));
          return String(v ?? '').toLowerCase().includes(q);
        });
      }

      return true;
    });
  }, [data, inferredCols, columnFilters, globalQuery, searchColumns]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const { key, dir } = sort;
    return [...filtered].sort((ra, rb) => {
      const va = getCellValue(ra, key);
      const vb = getCellValue(rb, key);
      const res = compare(va, vb);
      return dir === 'asc' ? res : -res;
    });
  }, [filtered, sort]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  const handleSort = (key: string, sortable?: boolean) => {
    if (sortable === false) return;
    setPage(1);
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  const cellPadding = dense ? 'px-3 py-1.5' : 'px-4 py-2.5';

  const moveColumn = (key: string, direction: 'left' | 'right') => {
    setColumnOrder(prev => {
      const index = prev.indexOf(key);

      if (index === -1) return prev;

      const targetIndex = direction === 'left'
        ? index - 1
        : index + 1;

      if (targetIndex < 0 || targetIndex >= prev.length) {
        return prev;
      }

      const next = [...prev];

      [next[index], next[targetIndex]] = [
        next[targetIndex],
        next[index]
      ];

      return next;
    });
  };

  return (
    <div className={`rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.03] ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 p-3 sm:p-4">
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowSearchColumns(v => !v)}
            className="
              min-w-[180px]
              flex items-center justify-between gap-2
              rounded-md border border-gray-300 dark:border-white/20
              bg-white dark:bg-gray-800
              px-3 py-2 text-sm
              text-gray-700 dark:text-gray-200
              hover:bg-gray-50 dark:hover:bg-gray-700
            "
          >
            <span>
              {searchColumns.length === 0
                ? 'Toutes les colonnes'
                : `${searchColumns.length} colonne${searchColumns.length > 1 ? 's' : ''}`}
            </span>

            <span className="text-xs">▼</span>
          </button>

          {showSearchColumns && (
            <div
              className="
                absolute left-0 top-full z-50 mt-2
                min-w-[240px]
                rounded-lg border border-gray-200 dark:border-white/10
                bg-white dark:bg-gray-800
                shadow-lg
                p-2
              "
            >
              <button
                type="button"
                onClick={() => setSearchColumns([])}
                className="
                  w-full text-left px-3 py-2 mb-1
                  rounded-md text-sm
                  text-gray-700 dark:text-gray-200
                  hover:bg-gray-100 dark:hover:bg-gray-700
                "
              >
                Toutes les colonnes
              </button>

              <div className="border-t border-gray-200 dark:border-white/10 my-1" />

              {visibleCols.map(c => {
                const key = String(c.key);
                const checked = searchColumns.includes(key);

                return (
                  <label
                    key={key}
                    className="
                      flex items-center gap-2
                      px-3 py-2
                      rounded-md cursor-pointer
                      text-sm text-gray-700 dark:text-gray-200
                      hover:bg-gray-100 dark:hover:bg-gray-700
                    "
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      className="accent-indigo-600"
                      onChange={() => {
                        setSearchColumns(prev =>
                          prev.includes(key)
                            ? prev.filter(k => k !== key)
                            : [...prev, key]
                        );

                        setPage(1);
                      }}
                    />

                    {c.header ?? key}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <input
          value={globalQuery}
          onChange={e => {
            setGlobalQuery(e.target.value);
            setPage(1);
          }}
          placeholder={
            searchColumns.length === 0
              ? 'Recherche dans toutes les colonnes…'
              : `Recherche dans ${searchColumns.length} colonne${searchColumns.length > 1 ? 's' : ''}…`
          }
          className="
            w-full sm:max-w-xs
            rounded-md border border-gray-300 dark:border-white/20
            bg-white/90 dark:bg-gray-800
            text-gray-900 dark:text-gray-100
            px-3 py-2 text-sm
            focus:border-indigo-500 focus:ring-indigo-500
          "
        />


        {/* Colonnes visibles */}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {orderedCols.map((c, index) => {
            const k = String(c.key);

            return (
              <div
                key={k}
                className="
                  inline-flex items-center gap-1
                  text-xs text-gray-600
                  bg-gray-100 dark:bg-white/5
                  dark:text-gray-200
                  px-2 py-1 rounded
                "
              >
                <input
                  type="checkbox"
                  className="accent-indigo-600"
                  checked={!hidden[k]}
                  onChange={e =>
                    setHidden(h => ({
                      ...h,
                      [k]: !e.target.checked
                    }))
                  }
                />

                <span>{c.header ?? k}</span>

                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() => moveColumn(k, 'left')}
                  className="px-1 disabled:opacity-20 hover:text-indigo-600"
                  title="Déplacer à gauche"
                >
                  ←
                </button>

                <button
                  type="button"
                  disabled={index === orderedCols.length - 1}
                  onClick={() => moveColumn(k, 'right')}
                  className="px-1 disabled:opacity-20 hover:text-indigo-600"
                  title="Déplacer à droite"
                >
                  →
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full border-t border-gray-100">
          <thead>
            <tr>
              {visibleCols.map(col => {
                const k = String(col.key);
                const sortable = col.sortable !== false;
                const isSorted = sort?.key === k;
                return (
                  <th
                      key={k}
                      style={{ width: col.width }}
                      className={`text-left text-xs font-semibold uppercase tracking-wide 
                        text-gray-700 dark:text-gray-200 ${cellPadding} 
                        ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}`}
                    >
                    <button
                      type="button"
                      onClick={() => handleSort(k, sortable)}
                      className={`inline-flex items-center gap-1 ${sortable ? 'hover:text-gray-700' : 'cursor-default'}`}
                    >
                      {col.header ?? k}
                      {sortable && (
                        <span className="text-[10px] opacity-70">
                          {isSorted ? (sort?.dir === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      )}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {pageData.length === 0 && (
              <tr>
                <td colSpan={visibleCols.length} className="p-8 text-center text-sm text-gray-500">
                  Aucune donnée.
                </td>
              </tr>
            )}

            {pageData.map((row, idx) => (
              <tr
                key={idx}
                className={`hover:bg-gray-50 dark:hover:bg-grey ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={() => onRowClick?.(row)}
              >
                {visibleCols.map(col => {
                  const k = String(col.key);
                  const raw = getCellValue(row, k);
                  const content = col.render ? col.render(raw, row) : (raw ?? '');
                  const align = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
                  return (
                    <td
                      key={k}
                      className={`${cellPadding} ${align} text-sm text-gray-800 dark:text-gray-100`}
                    >
                      {content}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between gap-3 p-3 sm:p-4">
        <div className="text-xs text-gray-500">
          {sorted.length === 0
            ? '0 ligne'
            : `Lignes ${((page - 1) * pageSize) + 1}–${Math.min(page * pageSize, sorted.length)} sur ${sorted.length}`}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="rounded border px-2 py-1 text-sm disabled:opacity-50 dark:text-gray-200"
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            Préc.
          </button>
          <span className="text-sm tabular-nums dark:text-gray-200">{page} / {pageCount}</span>
          <button
            className="rounded border px-2 py-1 text-sm disabled:opacity-50 dark:text-gray-200"
            disabled={page >= pageCount}
            onClick={() => setPage(p => Math.min(pageCount, p + 1))}
          >
            Suiv.
          </button>
        </div>
      </div>
    </div>
  );
}
